import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { readerApi, familyApi, reportApi, chapterApi, challengeApi, gamificationApi } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import LoadingSpinner from '../components/LoadingSpinner';
import ReaderSelector from '../components/reporting/ReaderSelector';
import BibleProgressCalendar from '../components/progress/BibleProgressCalendar';
import ProgressStats from '../components/progress/ProgressStats';
import ReaderStatsCard from '../components/dashboard/ReaderStatsCard';
import ManageReportsPage from './ManageReportsPage';
import UserPointsPage from './UserPointsPage';
import { hasSimplifiedSetup } from '../utils/userSetup';
import {
  countFinishedBooks,
  countChaptersInWindow,
  countUniqueChapters
} from '../utils/progressStats';
import './ProgressPage.css';
import YearSelector from '../components/progress/YearSelector';
import { isDateInWindow } from '../utils/progressStats';

const ProgressPage = () => {
  const { preferences } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const competitiveMode = Boolean(preferences?.competitiveMode);
  const hashSection = (location.hash || '#overview').replace('#', '') || 'overview';
  const section = hashSection === 'points' && !competitiveMode ? 'overview' : hashSection;

  const [readers, setReaders] = useState([]);
  const [families, setFamilies] = useState([]);
  const [chapters, setChapters] = useState([]);
  const [allReports, setReports] = useState([]);
  const [allRawReports, setRawReports] = useState([]);
  const [reportsLoading, setReportsLoading] = useState(false);
  const [year, setYear] = useState(String(new Date().getFullYear()));
  const reports = year === 'all' ? allReports : allReports.filter(report => isDateInWindow(report.readDate, `${year}-01-01`, `${year}-12-31`));
  const [refreshVersion, setRefreshVersion] = useState(0);
  const [readerProfile, setReaderProfile] = useState(null);
  const [challenge, setChallenge] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [selectedReaders, setSelectedReaders] = useState([]);
  const [readerMode, setReaderMode] = useState('primary');
  const [selectedFamily, setSelectedFamily] = useState(null);

  const rawReports = allRawReports.filter(report => selectedReaders.some(reader => reader.id === report.readerId) && (year === 'all' || isDateInWindow(report.reportedDate, `${year}-01-01`, `${year}-12-31`)));
  const rawChapters = rawReports.reduce((sum, report) => sum + report.chapterCount, 0);

  useEffect(() => {
    loadInitialData();
  }, []);

  useEffect(() => {
    let cancelled = false;
    setReports([]);
    setRawReports([]);
    setReportsLoading(selectedReaders.length > 0);
    setReaderProfile(null);
    if (selectedReaders.length > 0) {
      Promise.all([Promise.all(selectedReaders.map(reader => reportApi.getAllReports({ readerId: reader.id }))), reportApi.getAllRawReports()])
        .then(([responses, rawResponse]) => {
          if (cancelled) return;
          setReports(responses.flatMap(response => response.data.reports || []));
          setRawReports(rawResponse.data.reports || []);
          setError(null);
        })
        .catch(err => {
          if (cancelled) return;
          console.error('Failed to load progress data:', err);
          setError('Failed to load reading progress. Please try again.');
        }).finally(() => { if (!cancelled) setReportsLoading(false); });
      gamificationApi.getReaderProfile(selectedReaders[0].id)
        .then(response => { if (!cancelled) setReaderProfile(response.data.profile); })
        .catch(err => console.error('Failed to load reader profile:', err));
    }
    return () => { cancelled = true; };
  }, [selectedReaders, refreshVersion]);

  useEffect(() => {
    if (section === 'books') {
      document.getElementById('progress-books')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [section, loading]);

  const loadInitialData = async () => {
    try {
      setLoading(true);
      const [readersRes, familiesRes, chaptersRes, challengeRes] = await Promise.all([
        readerApi.getReaders(),
        familyApi.getFamilies(),
        chapterApi.getAllChapters(),
        challengeApi.getActive().catch(() => ({ data: {} }))
      ]);

      setReaders(readersRes.data.readers || []);
      setFamilies(familiesRes.data.families || []);
      setChapters(chaptersRes.data.chapters || []);
      setChallenge(challengeRes.data?.challenge || null);

      const primaryReader = readersRes.data.readers?.find(r => r.isPrimary);
      if (primaryReader) {
        setSelectedReaders([primaryReader]);
      }

      setError(null);
    } catch (err) {
      console.error('Failed to load initial data:', err);
      setError('Failed to load progress. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleReaderModeChange = (mode) => {
    setReaderMode(mode);
    setSelectedReaders([]);
    setSelectedFamily(null);

    if (mode === 'primary') {
      const primaryReader = readers.find(r => r.isPrimary);
      if (primaryReader) {
        setSelectedReaders([primaryReader]);
      }
    }
  };

  const handleFamilySelect = (family) => {
    setSelectedFamily(family);
    setSelectedReaders(family?.readers || []);
  };

  const handleReaderToggle = (reader) => {
    setSelectedReaders(prev => {
      const isSelected = prev.some(r => r.id === reader.id);
      if (isSelected) {
        return prev.filter(r => r.id !== reader.id);
      }
      return [...prev, reader];
    });
  };

  const getProgressStats = () => {
    if (!chapters.length) {
      return {
        totalChapters: 0,
        readChapters: 0,
        uniqueChaptersRead: 0,
        percentComplete: 0,
        oldTestamentRead: 0,
        newTestamentRead: 0,
        totalReports: 0
      };
    }

    const uniqueChaptersRead = new Set(reports.map(r => r.chapterId)).size;
    const oldTestamentChapters = new Set(
      reports.filter(r => r.chapter?.testament === 'Old').map(r => r.chapterId)
    ).size;
    const newTestamentChapters = new Set(
      reports.filter(r => r.chapter?.testament === 'New').map(r => r.chapterId)
    ).size;

    return {
      totalChapters: chapters.length,
      readChapters: reports.length,
      uniqueChaptersRead,
      percentComplete: Math.round((uniqueChaptersRead / chapters.length) * 100),
      oldTestamentRead: oldTestamentChapters,
      newTestamentRead: newTestamentChapters,
      totalReports: reports.length
    };
  };

  const getChapterReadCounts = () => {
    const readCounts = {};
    reports.forEach(report => {
      readCounts[report.chapterId] = (readCounts[report.chapterId] || 0) + 1;
    });
    return readCounts;
  };

  const showOverview = section !== 'reports' && section !== 'points';
  const goToSection = (next) => {
    navigate(next === 'overview' ? '/progress' : `/progress#${next}`);
  };

  const renderOverview = () => {
    if (loading || reportsLoading) {
      return (
        <div className="progress-page-pane loading">
          <LoadingSpinner size="large" message="Loading progress..." />
        </div>
      );
    }

    if (error) {
      return (
        <div className="error-message">
          <h2>Unable to load progress</h2>
          <p>{error}</p>
          <button onClick={() => { loadInitialData(); setRefreshVersion(value => value + 1); }} className="retry-button">
            Try Again
          </button>
        </div>
      );
    }

    const progressStats = getProgressStats();
    const chapterReadCounts = getChapterReadCounts();
    const isSimplifiedSetup = hasSimplifiedSetup(readers, families);
    const books = countFinishedBooks(chapters, chapterReadCounts);
    const derived = {
      booksFinished: books.finished,
      booksTotal: books.total,
      challengeChapters: countChaptersInWindow(reports, challenge?.startDate, challenge?.endDate),
      communityContributions: countUniqueChapters(reports),
      readingDays: new Set([...reports.map(report => report.readDate?.slice(0, 10)), ...rawReports.map(report => report.reportedDate?.slice(0, 10))].filter(Boolean)).size,
      chaptersRead: reports.length + rawChapters
    };

    return (
      <>
          {!isSimplifiedSetup && (
            <div className="progress-section">
              <ReaderSelector
                readers={readers}
                families={families}
                readerMode={readerMode}
                selectedReaders={selectedReaders}
                selectedFamily={selectedFamily}
                onReaderModeChange={handleReaderModeChange}
                onFamilySelect={handleFamilySelect}
                onReaderToggle={handleReaderToggle}
              />
            </div>
          )}

          {selectedReaders.length === 0 && (
            <div className="progress-section progress-empty">
              <h2>{readers.length === 0 ? 'No reader yet' : 'Choose a reader'}</h2>
              <p>
                {readers.length === 0
                  ? 'Add a reader under Family → Readers to start tracking progress.'
                  : 'Select a reader above to see their reading progress.'}
              </p>
              {readers.length === 0 && (
                <Link to="/family#readers" className="progress-empty-link">
                  Go to Family → Readers
                </Link>
              )}
            </div>
          )}

          {selectedReaders.length > 0 && (
            <>
              <div className="progress-section progress-section--stats">
                <YearSelector value={year} onChange={setYear} earliestYear={Math.min(new Date().getFullYear() - 1, ...[...allReports.map(report => report.readDate), ...allRawReports.map(report => report.reportedDate)].map(date => Number(date?.slice(0, 4))).filter(Number.isFinite))} />
                <ReaderStatsCard
                  reader={selectedReaders[0]}
                  profile={readerProfile}
                  derived={derived}
                  competitiveMode={false}
                  onRefresh={() => setRefreshVersion(value => value + 1)}
                />
              </div>

              {competitiveMode && (
                <p className="progress-competitive-links">
                  <Link to="/leaderboard">Leaderboard</Link>
                  <Link to="/progress#points">Points history</Link>
                </p>
              )}

              <div className="progress-section">
                <ProgressStats
                  stats={{ ...progressStats, totalReports: progressStats.totalReports + rawChapters, rawChapters }}
                  selectedReaders={selectedReaders}
                />
              </div>

              <div className="progress-section" id="progress-books">
                <BibleProgressCalendar
                  chapters={chapters}
                  chapterReadCounts={chapterReadCounts}
                />
              </div>
            </>
          )}
      </>
    );
  };

  return (
    <div className="progress-page">
      <div className="progress-header">
        <h1>My Progress</h1>
        <p>Track your Bible reading journey</p>
      </div>

      <div className="progress-tabs" role="tablist" aria-label="My Progress">
        <button
          type="button"
          role="tab"
          aria-selected={section === 'overview' || section === 'books'}
          className={`progress-tab ${(section === 'overview' || section === 'books') ? 'progress-tab--active' : ''}`}
          onClick={() => goToSection('overview')}
        >
          Overview
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={section === 'reports'}
          className={`progress-tab ${section === 'reports' ? 'progress-tab--active' : ''}`}
          onClick={() => goToSection('reports')}
        >
          Reports
        </button>

      </div>

      {section === 'reports' && <ManageReportsPage />}

      {section === 'points' && competitiveMode && <UserPointsPage />}

      {showOverview && renderOverview()}
    </div>
  );
};

export default ProgressPage;
